'use client';

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface Feature {
    icon: string;
    title: string;
    desc: string;
    color: string;
    bgColor: string;
}

const features: Feature[] = [
    { icon: 'AcademicCapIcon', title: 'Structured Live Programs', desc: 'Run full academic or vocational programs — SS classes, Medicine, Carpentry — all delivered as live, scheduled sessions.', color: 'text-primary', bgColor: 'bg-primary/10' },
    { icon: 'CalendarDaysIcon', title: 'Timetables & Scheduling', desc: 'Build class timetables for your programs. Students see exactly when each live session runs and attend on schedule.', color: 'text-orange', bgColor: 'bg-orange/10' },
    { icon: 'ChartBarIcon', title: 'Analytics & Reporting', desc: 'Track student attendance, live session engagement, and institutional KPIs from one dashboard.', color: 'text-purple', bgColor: 'bg-purple/10' },
    { icon: 'ClipboardDocumentCheckIcon', title: 'Assessment Builder', desc: 'Create tests, quizzes, and assignments to complement your live sessions with automated grading and detailed results.', color: 'text-green', bgColor: 'bg-green/10' },
    { icon: 'UserGroupIcon', title: 'Staff & Instructor Roles', desc: 'Manage multiple instructors, departments, and permissions. Each teacher runs their own live sessions under your institution.', color: 'text-coral', bgColor: 'bg-coral/10' },
    { icon: 'BanknotesIcon', title: 'Enrolment & Payments', desc: 'Manage student enrolment fees, bootcamp payments, and institutional billing with transparent financial reporting.', color: 'text-primary', bgColor: 'bg-primary/10' },
];

const orgTypes = [
    { label: 'Secondary Schools', icon: '🏫' },
    { label: 'Universities', icon: '🎓' },
    { label: 'Vocational Centres', icon: '🔧' },
    { label: 'Medical Training', icon: '🩺' },
    { label: 'Coding Academies', icon: '💻' },
    { label: 'Trade Schools', icon: '🏗️' },
];

export default function OrganizationsSection() {
    const sectionRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) entry.target.classList.add('visible');
                });
            },
            { threshold: 0.08 }
        );
        const els = sectionRef.current?.querySelectorAll('.reveal, .reveal-left, .reveal-right');
        els?.forEach((el) => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    return (
        <section id="organizations" ref={sectionRef} className="relative py-20 md:py-28 bg-foreground overflow-hidden">
            {/* Background */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-0 right-0 w-[700px] h-[700px] blob-primary opacity-20 translate-x-1/3 -translate-y-1/4" />
                <div className="absolute bottom-0 left-0 w-[500px] h-[500px] blob-accent opacity-15 -translate-x-1/4 translate-y-1/4" />
                <div
                    className="absolute inset-0 opacity-5"
                    style={{
                        backgroundImage: 'linear-gradient(to right, #ffffff 1px, transparent 1px), linear-gradient(to bottom, #ffffff 1px, transparent 1px)',
                        backgroundSize: '60px 60px',
                    }}
                />
            </div>

            <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8">
                {/* Header */}
                <div className="max-w-3xl mb-12 md:mb-16">
                    <div className="reveal mb-5">
                        <span className="pill-tag border border-white/20 text-white/70 bg-white/5 text-xs uppercase tracking-wider">
                            For organizations
                        </span>
                    </div>
                    <h2 className="reveal text-section-title font-extrabold text-white mb-5 delay-100">
                        Run your programs live, at scale.
                    </h2>
                    <p className="reveal text-lg text-white/60 font-body leading-relaxed max-w-2xl delay-200">
                        Whether you're running SS classes, a medical training program, a carpentry vocational school, or a coding bootcamp — Wisly gives your institution the tools to deliver structured, live learning experiences.
                    </p>

                    {/* Org type pills */}
                    <div className="reveal delay-300 flex flex-wrap gap-2 mt-6">
                        {orgTypes.map((org) => (
                            <span key={org.label} className="inline-flex items-center gap-1.5 pill-tag border border-white/15 text-white/70 bg-white/5 text-sm">
                                <span>{org.icon}</span>
                                {org.label}
                            </span>
                        ))}
                    </div>
                </div>

                {/* Two modes for orgs */}
                <div className="reveal delay-200 grid sm:grid-cols-2 gap-4 mb-10">
                    <div className="bg-white/5 border border-white/15 rounded-2xl p-6">
                        <div className="w-10 h-10 bg-primary/20 rounded-xl flex items-center justify-center text-xl mb-4">🏛️</div>
                        <h3 className="font-bold text-white text-base mb-2">Structured Programs</h3>
                        <p className="text-white/55 text-sm font-body leading-relaxed">
                            Run full academic or vocational programs — SS1 to SS3, Year 1 Medicine, Carpentry Level 2 — with a fixed timetable of live classes. Students enrol and attend on scheduled days.
                        </p>
                    </div>
                    <div className="bg-white/5 border border-white/15 rounded-2xl p-6">
                        <div className="w-10 h-10 bg-orange/20 rounded-xl flex items-center justify-center text-xl mb-4">🚀</div>
                        <h3 className="font-bold text-white text-base mb-2">Institutional Bootcamps</h3>
                        <p className="text-white/55 text-sm font-body leading-relaxed">
                            Launch intensive bootcamps — short, focused, live programs with a set timetable. Students pay to enrol and attend live sessions on the days your instructors are scheduled.
                        </p>
                    </div>
                </div>

                {/* Features grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 mb-12">
                    {features.map((feat, i) => (
                        <div
                            key={feat.title}
                            className="reveal card-hover cursor-pointer"
                            style={{ transitionDelay: `${i * 80}ms` }}
                        >
                            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 h-full group hover:bg-white/8 hover:border-white/20 transition-all duration-300">
                                <div className={`w-10 h-10 ${feat.bgColor} rounded-xl flex items-center justify-center mb-4`}>
                                    <Icon name={feat.icon as Parameters<typeof Icon>[0]['name']} size={20} className={feat.color} />
                                </div>
                                <h3 className="font-bold text-white text-base mb-2">{feat.title}</h3>
                                <p className="text-white/55 text-sm font-body leading-relaxed">{feat.desc}</p>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Stats bar */}
                <div className="reveal delay-300">
                    <div className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8 grid grid-cols-2 md:grid-cols-4 gap-6">
                        {[
                            { value: '1,200+', label: 'Students enrolled', color: 'text-primary' },
                            { value: '24', label: 'Active programs', color: 'text-orange' },
                            { value: '91%', label: 'Avg attendance', color: 'text-green' },
                            { value: '88%', label: 'Pass rate', color: 'text-purple' },
                        ].map((stat) => (
                            <div key={stat.label} className="text-center">
                                <p className={`text-3xl md:text-4xl font-extrabold ${stat.color} mb-1`}>{stat.value}</p>
                                <p className="text-white/50 text-sm font-body">{stat.label}</p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* CTA */}
                <div className="reveal delay-400 mt-10 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                    <div>
                        <p className="text-white font-semibold text-lg">Ready to run your institution on Wisly?</p>
                        <p className="text-white/50 text-sm font-body mt-1">
                            From secondary schools to vocational training centres — all live, all structured, all on one platform.
                        </p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
                        <Link
                            href="/studio"
                            className="group inline-flex items-center justify-center gap-2.5 px-7 py-3.5 bg-primary text-primary-foreground rounded-full text-base font-semibold hover:bg-primary/90 shadow-blue hover:-translate-y-0.5 transition-all duration-300"
                        >
                            Explore Wisly Studio
                            <svg className="w-4 h-4 transition-transform group-hover:translate-x-0.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                                <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </Link>
                        <Link
                            href="/early-access"
                            className="inline-flex items-center justify-center px-7 py-3.5 bg-white/10 text-white border border-white/20 rounded-full text-base font-semibold hover:bg-white/15 hover:-translate-y-0.5 transition-all duration-300"
                        >
                            Request Early Access
                        </Link>
                    </div>
                </div>
            </div>
        </section>
    );
}